import {
  Document,
  Font,
  Page,
  Image,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import moment from "moment";
import * as React from "react";
import { useEffect, useState } from "react";
import { loadImageConverteds } from "../../api/medecin";
import {
  formConfigDermatose,
  formConfigTummerCutane,
} from "../../config/formConfig";
import { getBodyPartLabel } from "../../config/bodyZones";
import { groupFilesByLesion } from "../../helper/lesions";

import RobotoRegular from "../../assets/fonts/Roboto-Regular.ttf";
import RobotoBold from "../../assets/fonts/Roboto-Bold.ttf";
import RobotoItalic from "../../assets/fonts/Roboto-Italic.ttf";
import logo from "../../assets/Logo.png";
export interface DocumentProps {
  data: any;
}

Font.register({
  family: "Roboto",
  fonts: [
    { src: RobotoRegular, fontWeight: 400 },
    { src: RobotoBold, fontWeight: 700 },
    { src: RobotoItalic, fontStyle: "italic", fontWeight: 400 },
  ],
});

const COLORS = {
  brand: "#2AA6C4",
  accent: "#E8722C",
  ink: "#1F2937",
  gray: "#7B6B6B",
  grayLight: "#9CA3AF",
  border: "#E5E7EB",
  boxBg: "#EAF4F8",
  boxBorder: "#2AA6C4",
  badgeGray: "#4B5563",
  badgeRed: "#C0392B",
  badgePriority: "#B9531D",
  badgeNotRequired: "#1D7A9C",
};

export const styles = StyleSheet.create({
  font: { fontFamily: "Roboto" },

  body: {
    paddingTop: 32,
    paddingBottom: 44,
    paddingHorizontal: 36,
    fontFamily: "Roboto",
    color: COLORS.ink,
  },

  masthead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 10,
    marginBottom: 18,
  },
  logo: { width: 20, height: 20, marginRight: 5 },
  brandRow: { flexDirection: "row", alignItems: "center" },
  avis: { color: "#6EC6E8" },
  doc: { color: "#F28C28" },
  brandName: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.brand,
    fontFamily: "Roboto",
  },
  brandUrl: { fontSize: 8, color: COLORS.gray, marginLeft: 4 },
  mastheadRight: { fontSize: 8, color: COLORS.gray, textAlign: "right" },

  eyebrow: {
    fontSize: 8,
    fontWeight: 700,
    color: COLORS.accent,
    letterSpacing: 1.5,
    marginBottom: 6,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    fontFamily: "Roboto",
    color: COLORS.ink,
  },
  titleItalicLine: {
    fontSize: 22,
    fontWeight: 700,
    fontStyle: "italic",
    color: COLORS.brand,
    marginBottom: 8,
  },
  introText: { fontSize: 10, color: "#6B7280", marginBottom: 14 },

  metaLabel: {
    fontSize: 7,
    color: COLORS.gray,
    textAlign: "right",
    letterSpacing: 0.5,
    marginTop: 5,
  },
  metaValue: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: "right",
    color: COLORS.ink,
    marginTop: 5,
  },

  resultBox: {
    backgroundColor: COLORS.boxBg,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.boxBorder,
    borderRadius: 2,
    padding: 14,
    marginBottom: 18,
  },
  resultHeaderRow: { flexDirection: "row", alignItems: "center" },
  eyebrowSmall: {
    fontSize: 7,
    fontWeight: 700,
    color: COLORS.accent,
    letterSpacing: 1,
    marginBottom: 6,
  },
  badge: {
    fontSize: 8,
    fontWeight: 700,
    color: "#FFFFFF",
    backgroundColor: COLORS.badgeGray,
    borderRadius: 9,
    paddingVertical: 3,
    paddingHorizontal: 8,
    marginRight: 8,
  },
  badgeUrgent: { backgroundColor: COLORS.badgeRed },
  badgeNonUrgent: { backgroundColor: COLORS.badgeGray },
  badgePriority: { backgroundColor: COLORS.badgePriority },
  badgeNotRequired: { backgroundColor: COLORS.badgeNotRequired },
  resultHeadline: { fontSize: 10, fontWeight: 700, color: COLORS.ink },
  resultDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#D8E9EF",
    marginVertical: 8,
  },
  resultRow: { flexDirection: "row" },
  resultRowLabel: {
    width: 90,
    fontSize: 7,
    fontWeight: 700,
    color: COLORS.brand,
    letterSpacing: 0.5,
    paddingTop: 1,
  },
  resultRowValue: { flex: 1 },
  resultRowValueBold: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.ink,
    marginBottom: 2,
  },
  resultRowValueText: { fontSize: 9.5, color: COLORS.ink },

  sectionHeader: {
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.accent,
    letterSpacing: 1,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 6,
    marginBottom: 10,
    marginTop: 5,
  },

  gridRow: { flexDirection: "row", width: "100%", marginBottom: 10 },
  gridItemFour: { width: "25%", paddingRight: 8 },
  gridItem: { width: "50%", paddingRight: 8 },
  fieldLabel: {
    fontSize: 7,
    color: COLORS.gray,
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  fieldValue: { fontSize: 10.5, fontWeight: 700, color: COLORS.ink },
  fieldValueMuted: {
    fontSize: 10,
    fontStyle: "italic",
    color: COLORS.grayLight,
  },
  grid: { flexDirection: "row", flexWrap: "wrap" },

  cardRow: { flexDirection: "row", justifyContent: "space-between" },
  card: {
    width: "48%",
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    padding: 10,
  },
  cardEyebrow: {
    fontSize: 7,
    fontWeight: 700,
    color: COLORS.brand,
    letterSpacing: 1,
    marginBottom: 4,
  },
  cardName: {
    fontSize: 11.5,
    fontWeight: 700,
    marginBottom: 6,
    color: COLORS.ink,
  },
  cardLine: { flexDirection: "row", marginBottom: 2 },
  cardLineLabel: { fontSize: 7.5, color: COLORS.gray, width: 40 },
  cardLineValue: { fontSize: 9, fontWeight: 700, color: COLORS.ink },

  answerGrid: { flexDirection: "row", flexWrap: "wrap" },
  answerItem: {
    width: "50%",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingRight: 8,
  },
  answerPill: {
    fontSize: 6.5,
    fontWeight: 700,
    color: COLORS.brand,
    borderWidth: 1,
    borderColor: COLORS.brand,
    borderRadius: 8,
    paddingVertical: 2,
    paddingHorizontal: 6,
    marginRight: 6,
    minWidth: 26,
    textAlign: "center",
  },
  answerPillMuted: {
    fontSize: 6.5,
    fontWeight: 700,
    color: COLORS.grayLight,
    borderWidth: 1,
    borderColor: COLORS.grayLight,
    borderRadius: 8,
    paddingVertical: 2,
    paddingHorizontal: 6,
    marginRight: 6,
    minWidth: 26,
    textAlign: "center",
  },
  answerText: { fontSize: 9, color: COLORS.ink, flexShrink: 1 },
  freeTextLine: { fontSize: 9, marginBottom: 3 },
  freeTextField: { marginBottom: 8 },
  freeTextLabel: { fontWeight: 700 },
  freeTextMuted: { fontStyle: "italic", color: COLORS.gray },

  footerBar: {
    position: "absolute",
    bottom: 20,
    left: 36,
    right: 36,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7.5,
    color: COLORS.gray,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 6,
  },
  footerBrand: { fontWeight: 700, color: COLORS.brand },

  lesionCard: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    padding: 12,
    marginBottom: 14,
  },
  lesionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    paddingBottom: 8,
  },
  lesionNumber: {
    width: 20,
    height: 20,
    borderRadius: 8,
    backgroundColor: COLORS.brand,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },

  lesionNumberText: {
    color: "#FFFFFF",
    fontSize: 8,
    fontWeight: 700,
    textAlign: "center",
  },

  lesionImagesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    width: "100%",
  },
  lesionPhoto: {
    width: "48%",
    marginBottom: 10,
  },
  lesionImageWrap: {
    width: "100%",
    height: 180,
    marginBottom: 4,
  },

  lesionImageCaption: {
    fontSize: 7.5,
    fontStyle: "italic",
    color: COLORS.gray,
    marginTop: 0,
    whiteSpace: "nowrap",
  },
  lesionTitle: { fontSize: 11.5, fontWeight: 700, color: COLORS.brand },
  lesionMetaRow: { flexDirection: "row", marginBottom: 8 },
  lesionMetaItem: { flex: 1 },

  lesionImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: 3,
  },
  lesionDetailBox: { marginTop: 8 },
  lesionSubEntry: { marginBottom: 10 },
  lesionSubDivider: {
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    marginTop: 10,
    marginBottom: 10,
  },
  stepRow: { flexDirection: "row", marginBottom: 10 },
  stepDivider: {
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    marginLeft: 32,
    marginBottom: 10,
  },
  stepNumber: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: COLORS.brand,
    color: "#FFFFFF",
    fontSize: 8,
    fontWeight: 700,
    textAlign: "center",
    lineHeight: 1.3,
    marginRight: 8,
  },
  stepText: { fontSize: 9.5, flex: 1, lineHeight: 1.4 },
  stepTextBold: { fontWeight: 700 },
  stepNumberText: {
    width: 24,
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.brand,
    textAlign: "center",
    marginRight: 8,
  },
  conduiteBlock: {},

  noticeBox: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    padding: 10,
    marginTop: 12,
    backgroundColor: "#FAFAFA",
  },
  noticeTitle: { fontSize: 8.5, fontWeight: 700, marginBottom: 3 },
  noticeText: {
    fontSize: 8,
    color: COLORS.gray,
    marginBottom: 5,
    lineHeight: 1.4,
  },
  noticeItalic: {
    fontSize: 7.5,
    fontStyle: "italic",
    color: COLORS.gray,
    marginTop: 4,
  },

  pageNumber: {
    position: "absolute",
    fontSize: 8,
    bottom: 20,
    right: 36,
    color: COLORS.gray,
  },
});

const stripHtmlAndStyles = (html: string = "") =>
  html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/ style="[^"]*"/gi, "")
    .replace(/font-family\s*:\s*[^;>"]*;?/gi, "")
    .replace(/<[^>]*>/g, "");

const parseImage = (specialite, userId, filePath) => ({
  original: loadImageConverteds(specialite, userId, "preview", filePath),
});

// Interrogatoire fields answered by Oui / Non / Inconnue: the only ones
// rendered as boxes, every other field is rendered as free text.
const YES_NO_VALUES = ["oui", "non", "inconnue"];
const FORM_FIELDS: Record<string, any> = Object.fromEntries(
  [...formConfigDermatose, ...formConfigTummerCutane].map((f: any) => [
    f.field,
    f,
  ])
);
const isYesNoField = (field: string, value: any) => {
  const conf = FORM_FIELDS[field];
  if (conf?.options) {
    return conf.options.every((o: any) =>
      YES_NO_VALUES.includes(String(o.value).trim().toLowerCase())
    );
  }
  return YES_NO_VALUES.includes(String(value ?? "").trim().toLowerCase());
};
const isEmptyAnswer = (value: any) =>
  Array.isArray(value)
    ? value.filter((v) => String(v).trim()).length === 0
    : ["", "undefined", "null"].includes(String(value ?? "").trim());

const PRIORITY_RECOMMENDATIONS = [
  "consultation dermatologique",
  "exérèse indispensable",
];

const NumberBadge: React.FC<{ n: number | string }> = ({ n }) => (
  <View style={styles.lesionNumber}>
    <Text style={styles.lesionNumberText}>{n}</Text>
  </View>
);

const Footer: React.FC<{ specialite?: string }> = ({ specialite }) => (
  <View style={styles.footerBar} fixed>
    <Text>
      <Text style={styles.avis}>Avis</Text>
      <Text style={styles.doc}>Doc</Text>· avisdoc.fr
    </Text>
    <Text
      render={({ pageNumber }) =>
        `${moment().format("MMMM YYYY")} · ${String(pageNumber).padStart(2, "0")}`
      }
    />
  </View>
);

const TestDocument: React.FC<DocumentProps> = ({ data }) => {
  const [parsedImages, setParsedImages] = useState<string[]>([]);

  const propertyNames: [string, string][] = Object.entries(
    data.interrogatoire?.other || {},
  );

  useEffect(() => {
    if (data.files.length > 0) {
      const imgs = [] as any;
      data.files.map((img) => {
        imgs.push(parseImage(img.specialite, img.patientId, img.filePath));
      });
      setParsedImages(imgs);
    }
  }, []);

  function splitString(stringToSplit, separator) {
    var arrayOfStrings = stringToSplit.split(separator);
    return arrayOfStrings;
  }

  const dataToMap = () => {
    const mapped = propertyNames.map((e) => {
      if (e[1] === "undefined") {
        return { label: e[0], value: "Inconnue" };
      }
      if (typeof e[1] === "string" && e[1].indexOf(",") > 0)
        return { label: e[0], value: splitString(e[1], ",") };
      else return { label: e[0], value: e[1] };
    });
    return mapped;
  };

  // Zone → lesions → photos, rebuilt from the per-photo metadata
  const bodyPartSections = React.useMemo(() => {
    const sections: {
      bodyPart: string;
      lesions: ReturnType<typeof groupFilesByLesion>;
    }[] = [];
    const sectionIndexByZone: Record<string, number> = {};

    groupFilesByLesion(data.files || []).forEach((lesion) => {
      const key = lesion.bodyPart || "__unspecified";
      if (!(key in sectionIndexByZone)) {
        sectionIndexByZone[key] = sections.length;
        sections.push({
          bodyPart:
            getBodyPartLabel(lesion.bodyPart) || "Localisation non renseignée",
          lesions: [],
        });
      }
      sections[sectionIndexByZone[key]].lesions.push(lesion);
    });

    return sections;
  }, [data]);

  const answers = dataToMap().filter(
    (e) => e.label !== "meta" && e.label !== "Commentaires libres"
  );
  const yesNoAnswers = answers.filter(
    (e) => !Array.isArray(e.value) && isYesNoField(e.label, e.value)
  );
  const freeTextAnswers = answers.filter(
    (e) =>
      !yesNoAnswers.includes(e) &&
      !(FORM_FIELDS[e.label]?.condition && isEmptyAnswer(e.value))
  );
  const freeComment = data.interrogatoire?.other?.["Commentaires libres"];
  const questionLabel = (field: string) =>
    (FORM_FIELDS[field]?.label || field).trim();
  const isUrgent = !!data.Reponse?.urgence;

  const recommendationLabel = String(
    data.Reponse?.recommendation?.c || ""
  ).trim();
  const recommendationDelay = String(
    data.Reponse?.recommendation?.nbrJrs || ""
  ).trim();
  const recommendationText = recommendationLabel
    ? `${recommendationLabel}${
        recommendationDelay
          ? ` dans un délai de ${recommendationDelay} jours`
          : ""
      }.`
    : "";
  const isPriority = PRIORITY_RECOMMENDATIONS.includes(
    recommendationLabel.toLowerCase()
  );

  const isDocReqMedecin =
    !data.docReq?.requrentType ||
    data.docReq.requrentType === "medecinRequrent";
  const docReqLabel = isDocReqMedecin
    ? "Docteur demandeur"
    : "Praticien demandeur";
  const docReqCivility = isDocReqMedecin
    ? "Dr "
    : data.docReq?.gender === "female"
      ? "Mme "
      : "M. ";

  if (data) {
    return (
      <Document>
        <Page style={styles.body}>
          <View style={styles.masthead} fixed>
            <View style={styles.brandRow}>
              <Image src={logo} style={styles.logo} />
              <Text style={styles.brandName}>
                <Text style={styles.avis}>Avis</Text>
                <Text style={styles.doc}>Doc</Text>
              </Text>
              <Text style={styles.brandUrl}>avisdoc.fr</Text>
            </View>
            <Text style={styles.mastheadRight}>
              Compte-rendu de télé-expertise · {data.specialite}
            </Text>
          </View>

          <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <View style={{ width: "62%" }}>
              <Text style={styles.eyebrow}>COMPTE-RENDU D'AVIS SPÉCIALISÉ</Text>
              <Text style={styles.title}>Avis de télé-expertise,</Text>
              <Text style={styles.titleItalicLine}>{data.specialite}</Text>
              <Text style={styles.introText}>
                Avis asynchrone rendu à la demande :{" "}
                {data.docReq?.firstName} {data.docReq?.lastName}.
              </Text>
            </View>
            <View style={{ width: "32%", marginLeft: "auto" }}>
              <Text style={styles.metaLabel}>RÉFÉRENCE DOSSIER</Text>
              <Text style={styles.metaValue}>{data._id}</Text>
              <Text style={styles.metaLabel}>MOTIF</Text>
              <Text style={styles.metaValue}>{data.type}</Text>
              <Text style={styles.metaLabel}>DEMANDE CRÉÉE LE</Text>
              <Text style={styles.metaValue}>
                {moment(data.createdAt).format("L")}
              </Text>
              <Text style={styles.metaLabel}>IMAGES TRANSMISES</Text>
              <Text style={styles.metaValue}>{data?.files?.length || 0}</Text>
            </View>
          </View>

          <View style={styles.resultBox} wrap={false}>
            <Text style={styles.eyebrowSmall}>
              RÉSULTAT DE LA TÉLÉ-EXPERTISE
            </Text>
            {recommendationText ? (
              <View style={styles.resultHeaderRow}>
                <Text
                  style={[
                    styles.badge,
                    isPriority ? styles.badgePriority : styles.badgeNotRequired,
                  ]}
                >
                  {isPriority ? "PRIORITÉ" : "NON REQUIS"}
                </Text>
                <Text style={styles.resultHeadline}>{recommendationText}</Text>
              </View>
            ) : (
              <View style={styles.resultHeaderRow}>
                <Text
                  style={[
                    styles.badge,
                    isUrgent ? styles.badgeUrgent : styles.badgeNonUrgent,
                  ]}
                >
                  {isUrgent ? "URGENT" : "NON URGENT"}
                </Text>
                <Text style={styles.resultHeadline}>
                  {isUrgent
                    ? "Cette demande nécessite une intervention urgente."
                    : "Aucune action immédiate requise."}
                </Text>
              </View>
            )}

            <View style={styles.resultDivider} />

            <View style={styles.resultRow}>
              <Text style={styles.resultRowLabel}>AVIS</Text>
              <View style={styles.resultRowValue}>
                <Text style={styles.resultRowValueText}>
                  {stripHtmlAndStyles(
                    data.Reponse.response || data.Reponse || "",
                  )}
                </Text>
                {data.Reponse.diagnosticPreliminaire ? (
                  <Text style={[styles.resultRowValueText, { marginTop: 4 }]}>
                    Diagnostic préliminaire :{" "}
                    {data.Reponse.diagnosticPreliminaire}
                  </Text>
                ) : null}
              </View>
            </View>

          </View>

          <Text style={styles.sectionHeader} minPresenceAhead={40}>
            01 · PATIENT
          </Text>
          <View style={styles.gridRow} wrap={false}>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>NOM / PRÉNOM</Text>
              <Text style={styles.fieldValue}>
                {data.patientId.lastName} {data.patientId.firstName}
              </Text>
            </View>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>SEXE</Text>
              <Text style={styles.fieldValue}>
                {data.patientId.gender === "male" ? "Homme" : "Femme"}
              </Text>
            </View>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>DATE DE NAISSANCE</Text>
              <Text style={styles.fieldValue}>
                {moment(data.patientId.birthday).format("L")}
              </Text>
            </View>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>NUMÉRO DE SÉCURITÉ SOCIALE</Text>
              <Text style={styles.fieldValue}>
                {data.patientId.numeroSecurite}
              </Text>
            </View>
          </View>

          <View style={styles.gridRow} wrap={false}>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>TÉLÉPHONE</Text>
              <Text style={styles.fieldValue}>{data.patientId.phone}</Text>
            </View>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>ADRESSE POSTALE</Text>
              <Text style={styles.fieldValue}>
                {data.patientId.adresse} {data.patientId.codePostale}
              </Text>
            </View>
            <View style={styles.gridItemFour}>
              <Text style={styles.fieldLabel}>ORGANISME PAYEUR</Text>
              {data.patientId.organismeSecuSociale ? (
                <Text style={styles.fieldValue}>
                  {data.patientId.organismeSecuSociale}
                </Text>
              ) : (
                <Text style={styles.fieldValueMuted}>Non renseigné</Text>
              )}
            </View>
            <View style={styles.gridItemFour} />
          </View>

          <Text style={styles.sectionHeader} minPresenceAhead={60}>
            02 · PROFESSIONNELS DE SANTÉ
          </Text>
          <View style={styles.cardRow} wrap={false}>
            <View style={styles.card}>
              <Text style={styles.cardEyebrow}>REQUÉRANT</Text>
              <Text style={styles.cardName}>
                {docReqCivility}
                {data.docReq.lastName + " " + data.docReq.firstName}
              </Text>
              <View style={styles.cardLine}>
                <Text style={styles.cardLineLabel}>RPPS</Text>
                <Text style={styles.cardLineValue}>
                  {data.docReq.numRPPS || "N/A"}
                </Text>
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.cardEyebrow}>
                EXPERT {String(data.specialite || "").toUpperCase()}
              </Text>
              <Text style={styles.cardName}>
                Dr {data?.docExpert?.firstName} {data?.docExpert?.lastName}
              </Text>
              <View style={styles.cardLine}>
                <Text style={styles.cardLineLabel}>RPPS</Text>
                <Text style={styles.cardLineValue}>
                  {data?.docExpert?.numRPPS || "N/A"}
                </Text>
              </View>
            </View>
          </View>

          <Text style={styles.sectionHeader} minPresenceAhead={60}>
            03 · INTERROGATOIRE ET TERRAIN
          </Text>
          <View style={styles.answerGrid}>
            {yesNoAnswers.map((e, index) => {
              const value = String(e.value ?? "").trim().toLowerCase();
              const isUnknown = !["oui", "non"].includes(value);
              return (
                <View key={index} style={styles.answerItem} wrap={false}>
                  <Text
                    style={
                      isUnknown ? styles.answerPillMuted : styles.answerPill
                    }
                  >
                    {isUnknown ? "N.R." : value === "oui" ? "OUI" : "NON"}
                  </Text>
                  <Text style={styles.answerText}>{questionLabel(e.label)}</Text>
                </View>
              );
            })}
          </View>

          {freeTextAnswers.map((e, index) => (
            <View key={index} style={styles.freeTextField} wrap={false}>
              <Text style={styles.fieldLabel}>
                {questionLabel(e.label).toUpperCase()}
              </Text>
              {isEmptyAnswer(e.value) ? (
                <Text style={styles.freeTextMuted}>Non renseigné</Text>
              ) : (
                <Text style={styles.answerText}>
                  {Array.isArray(e.value)
                    ? e.value.map((v) => String(v).trim()).filter(Boolean).join(", ")
                    : String(e.value)}
                </Text>
              )}
            </View>
          ))}

          <View wrap={false}>
            <Text style={styles.freeTextLine}>
              <Text style={styles.freeTextLabel}>Commentaires libres : </Text>
              {isEmptyAnswer(freeComment) ? (
                <Text style={styles.freeTextMuted}>non renseignés.</Text>
              ) : (
                <Text>{String(freeComment)}</Text>
              )}
            </Text>
          </View>

          <Text style={styles.sectionHeader} minPresenceAhead={80}>
            04 · ZONES DOCUMENTÉES
          </Text>

          {bodyPartSections.map((section, sectionIndex) => (
            <View key={sectionIndex} style={styles.lesionCard}>
              <View style={styles.lesionHeaderRow} wrap={false}>
                <NumberBadge n={sectionIndex + 1} />
                <Text style={styles.lesionTitle}>{section.bodyPart}</Text>
              </View>

              {section.lesions.map((lesion, lesionIndex) => (
                <View key={lesionIndex} style={styles.lesionSubEntry}>
                  {lesionIndex > 0 && <View style={styles.lesionSubDivider} />}

                  <View style={styles.lesionMetaRow} wrap={false}>
                    {section.lesions.length > 1 && (
                      <View style={styles.lesionMetaItem}>
                        <Text style={styles.fieldLabel}>LÉSION</Text>
                        <Text style={styles.fieldValue}>{lesionIndex + 1}</Text>
                      </View>
                    )}
                    <View style={styles.lesionMetaItem}>
                      <Text style={styles.fieldLabel}>TAILLE</Text>
                      <Text style={styles.fieldValue}>
                        {lesion.taille ? `${lesion.taille} mm` : "Non renseignée"}
                      </Text>
                    </View>
                    <View style={styles.lesionMetaItem}>
                      <Text style={styles.fieldLabel}>ANCIENNETÉ</Text>
                      <Text style={styles.fieldValue}>
                        {lesion.anciennete || "Non renseignée"}
                      </Text>
                    </View>
                    <View style={styles.lesionMetaItem}>
                      <Text style={styles.fieldLabel}>DOULEUR</Text>
                      <Text style={styles.fieldValue}>
                        {lesion.douleur || "Non renseignée"}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.lesionImagesGrid}>
                    {lesion.items.map(({ file, index }) => (
                      <View key={index} style={styles.lesionPhoto} wrap={false}>
                        <View style={styles.lesionImageWrap}>
                          <Image
                            style={styles.lesionImage}
                            src={{
                              uri: (parsedImages[index] as any)?.original,
                              method: "GET",
                              headers: {},
                              body: "",
                            }}
                          />
                        </View>
                        <Text style={styles.lesionImageCaption}>
                          Photo {index + 1}
                        </Text>
                        {file?.message ? (
                          <Text style={styles.resultRowValueText}>
                            {file.message}
                          </Text>
                        ) : null}
                      </View>
                    ))}
                  </View>
                </View>
              ))}
            </View>
          ))}

          <View wrap={false}>
            <Text style={styles.sectionHeader}>
              05 · CONDUITE À TENIR, INFORMATIONS PATIENTE
            </Text>

            <View style={styles.stepRow}>
              <Text style={styles.stepNumberText}>1</Text>
              <Text style={styles.stepText}>
                <Text style={styles.stepTextBold}>
                  {isUrgent
                    ? "Une consultation urgente est nécessaire : "
                    : "Aucune consultation urgente n'est nécessaire : "}
                </Text>
                {isUrgent
                  ? "au moins une lésion nécessite une prise en charge rapide."
                  : "aucune des lésions transmises ne paraît suspecte à ce stade."}
              </Text>
            </View>
            <View style={styles.stepDivider} />
            <View style={styles.stepRow}>
              <Text style={styles.stepNumberText}>2</Text>
              <Text style={styles.stepText}>
                <Text style={styles.stepTextBold}>
                  Surveillez la ou les lésions :{" "}
                </Text>
                vérifiez qu'elles n'évoluent pas (taille, couleur, bords,
                relief, saignement).
              </Text>
            </View>

            <View style={styles.stepDivider} />
            <View style={styles.stepRow}>
              <Text style={styles.stepNumberText}>3</Text>
              <Text style={styles.stepText}>
                <Text style={styles.stepTextBold}>
                  Reconsultez en cas de changement :{" "}
                </Text>
                si vous constatez une modification, contactez votre médecin. Un
                nouvel avis ou une consultation en présentiel sera alors
                nécessaire.
              </Text>
            </View>

            <View style={styles.noticeBox}>
              <Text style={styles.noticeTitle}>Portée de l'avis</Text>
              <Text style={styles.noticeText}>
                Cet avis de télé-expertise est rendu à distance, sur la base des
                éléments cliniques et photographiques transmis par le médecin
                requérant. Il ne se substitue pas à un examen clinique direct ni
                à une consultation présentielle. La responsabilité de la prise
                en charge et du suivi demeure celle du médecin requérant. En cas
                d'aggravation ou d'élément nouveau, un nouvel avis ou une
                consultation présentielle doit être sollicité.
              </Text>
              <Text style={styles.noticeTitle}>Confidentialité</Text>
              <Text style={styles.noticeText}>
                Document contenant des données de santé à caractère personnel,
                hébergé chez un Hébergeur de Données de Santé (HDS) certifié,
                traité conformément au RGPD. Diffusion strictement limitée à la
                patiente et au médecin requérant.
              </Text>
            </View>
            <View style={styles.stepDivider} />
          </View>

          <Footer />
        </Page>
      </Document>
    );
  } else
    return (
      <Document>
        <Page size="A4">
          <View>
            <Text>Le Pdf n'a pas pu être généré</Text>
          </View>
        </Page>
      </Document>
    );
};

export default React.memo(TestDocument);
